import React, { useState } from "react";
import styled from "styled-components/macro";
import { Box as MuiBox, CircularProgress } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import { TabList as MuiTabList } from "@mui/lab";
import TabPanel from "@mui/lab/TabPanel";
import RefreshIcon from "@mui/icons-material/Refresh";

const Box = styled(MuiBox)``;

const StyledTabContext = styled(MuiBox)``;

const TabList = styled(MuiTabList)`
  span {
    background-color: #192f67;
  }
  button {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    text-align: center;
    font-family: "Montserrat", sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .Mui-selected {
    color: var(--light-primary-main, #192f67);
  }
`;

interface TabItem {
  label: string;
  value: string;
  component: React.ReactNode;
}

interface TabPageProps {
  tabs: TabItem[];
  defaultTab?: string;
  showRefresh?: boolean;
  onRefresh?: () => void;
  isSaveDisabled?: boolean;
  onTabChange?: (newValue: string) => void;
  tabsRef?: React.RefObject<HTMLDivElement>;
}

const TabPage: React.FC<TabPageProps> = ({
  tabs,
  defaultTab = "1",
  showRefresh = true,
  onRefresh,
  isSaveDisabled = true,
  onTabChange,
  tabsRef = null
}) => {
  const [value, setValue] = useState(defaultTab);
  const [refreshing, setRefreshing] = useState(false);

  // Notify parent of initial active tab after mount
  React.useEffect(() => {
    onTabChange?.(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (isSaveDisabled) {
      setValue(newValue);
      onTabChange?.(newValue);
    }
  };

  const handleRefresh = async () => {
    if (!isSaveDisabled) {
      // Handle unsaved changes case
      return;
    }
    setRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 1000);
    }
  };

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <TabContext value={value}>
        <StyledTabContext
          sx={{
            display: "flex",
            cursor: "pointer",
            alignItems: "center"
          }}
        >
          <TabList
            ref={tabsRef as any}
            variant="scrollable"
            onChange={handleChange}
            aria-label="tab list"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </TabList>
          {showRefresh && (
            <Box sx={{ cursor: "pointer" }}>
              {refreshing ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                <RefreshIcon onClick={handleRefresh} />
              )}
            </Box>
          )}
        </StyledTabContext>
        {tabs.map((tab) => (
          <TabPanel key={tab.value} value={tab.value}>
            {tab.component}
          </TabPanel>
        ))}
      </TabContext>
    </Box>
  );
};

export default TabPage;
